import { Skeleton } from "@credit-acceptance/uikit";
import { useTranslation } from "react-i18next";

import { getErrorMessage } from "../../lib/query/errors";
import { useDscNotes } from "./api/useDscNotes";
import { formatChangeDate } from "./formatChangeDate";

interface PreApprovalChangeHistoryTabProperties {
  approvalId: string;
}

/*
 * DSC Pre-approval Change History tab (OR-21039). Renders the recap-service
 * change-history rows — Date Time / Attribute / Previous Value / Current Value —
 * newest-first (server order), no pagination. caps2 step-6 showed this as a
 * popup; the Workbench renders it as an Internal / Audit drawer tab.
 *
 * Read-only for all six WorkbenchRoles (no role gate). The scroll container is
 * the parent tab panel (`flex-1 overflow-y-auto`); the table header stays
 * pinned via `sticky` so column labels remain visible on long histories.
 */
function PreApprovalChangeHistoryTab({ approvalId }: PreApprovalChangeHistoryTabProperties) {
  const { t } = useTranslation();
  const { data, isLoading, isError, error } = useDscNotes(approvalId);

  if (isLoading) {
    return (
      <div role="status" aria-label={t("audit.preApprovalHistory.loading")} className="px-1 py-3">
        <Skeleton loading active paragraph={{ rows: 6 }} />
      </div>
    );
  }

  if (isError) {
    return (
      <p role="alert" className="text-destructive px-1 py-3 text-xs">
        {t("audit.preApprovalHistory.loadError", { message: getErrorMessage(error) })}
      </p>
    );
  }

  const rows = data ?? [];

  if (rows.length === 0) {
    return <p className="text-muted-foreground px-1 py-3 text-xs">{t("audit.preApprovalHistory.empty")}</p>;
  }

  // No `overflow-hidden` on the wrapper: an overflow clip between the sticky
  // <thead> and the scrolling tab panel would cancel the sticky header.
  return (
    <div className="border-border rounded-lg border">
      <table className="w-full border-collapse text-xs">
        <caption className="sr-only">{t("audit.preApprovalHistory.caption")}</caption>
        <thead>
          {/* Background + sticky live on each <th>, not the <tr>: under
              `border-collapse` Chrome does not paint a <tr>/<thead> background
              for a sticky header, so scrolled rows would show through it. */}
          <tr className="text-foreground">
            <th
              scope="col"
              className="bg-section-header border-border sticky top-0 border-b px-2 py-1.5 text-left font-semibold"
            >
              {t("audit.preApprovalHistory.columns.dateTime")}
            </th>
            <th
              scope="col"
              className="bg-section-header border-border sticky top-0 border-b px-2 py-1.5 text-left font-semibold"
            >
              {t("audit.preApprovalHistory.columns.attribute")}
            </th>
            <th
              scope="col"
              className="bg-section-header border-border sticky top-0 border-b px-2 py-1.5 text-left font-semibold"
            >
              {t("audit.preApprovalHistory.columns.previousValue")}
            </th>
            <th
              scope="col"
              className="bg-section-header border-border sticky top-0 border-b px-2 py-1.5 text-left font-semibold"
            >
              {t("audit.preApprovalHistory.columns.currentValue")}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            // Change-history rows have no stable server id; the newest-first
            // ordering is fixed for a given fetch, so the index is a stable key
            // within a render pass. Safe here because the list is read-only
            // (never reordered or spliced client-side).
            <tr key={index} className="border-border border-t">
              <td className="text-foreground whitespace-nowrap px-2 py-1.5 align-top">{formatChangeDate(row.date)}</td>
              <td className="text-foreground px-2 py-1.5 align-top">{row.attributeName}</td>
              <td className="text-muted-foreground px-2 py-1.5 align-top">{row.oldValue}</td>
              <td className="text-foreground px-2 py-1.5 align-top">{row.newValue}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default PreApprovalChangeHistoryTab;
